import { NetworkStatus } from '@apollo/client';
import * as React from 'react';
import { QueryResult } from '@apollo/client';
import { Query } from '@apollo/client/react/components';
import styled from 'styled-components';

import { GET_AIR_DATA_OF_ALL_GROUPS } from '../../afb-graphql-api/gqls/air-data-poll.gql';
import withAfbApollo from '../../afb-graphql-api/withAfbApollo';
import ScoreDotLabel from '../../ui/components/ScoreDotLabel';
import SensorDotLabel from '../../ui/components/SensorDotLabel';
import Snapshot from '../../ui/components/Snapshot';
import { scoreToCondition } from '../../utils/score-to-condition.util';
import { airDataTimeInterval } from '../../utils/time-interval.util';
import { Measurement } from '../enums/measurement.enum';
import {
  AverageSensorIndex,
  AverageSensorValue,
  GroupWithAirData,
  OrgData,
  Sensors,
  SnapshotEntity,
} from '../models/air-data-poll.model';

interface SnapshotGridData {
  org: OrgData;
}

interface SnapshotGridVariables {
  orgId: string;
}

interface States {
  variables: SnapshotGridVariables;
}

const StSnapshotGridContainer = styled.section`
  display: flex;
  justify-content: space-between;
`;

class SnapshotGridContainer extends React.Component<any, States> {
  AWAIR_OMNI_SENSOR_TYPES: Measurement[] = [
    Measurement.Temperature,
    Measurement.Humidity,
    Measurement.CO2,
    Measurement.Chemicals,
    Measurement.PM25,
  ];

  timer: NodeJS.Timeout;

  constructor(props: any) {
    super(props);

    this.state = {
      variables: this.getVariables(),
    };
  }

  componentWillUnmount() {
    this.releaseTimer();
  }

  getVariables = (): SnapshotGridVariables => {
    return {
      orgId: `${process.env.REACT_APP_ORGANIZATION_ID}`,
    };
  };

  getSnapshotSensors = (
    averageSensorValues: AverageSensorValue[],
    averageSensorIndices: AverageSensorIndex[]
  ): Sensors => {
    const sensorValues: { [sensorType: string]: number } = averageSensorValues.reduce(
      (acc: { [sensorType: string]: number }, sensorValue: AverageSensorValue) => {
        return {
          ...acc,
          [sensorValue.component]: sensorValue.averageValue,
        };
      },
      {}
    );
    const sensorIndices: { [sensorType: string]: number } = averageSensorIndices.reduce(
      (acc: { [sensorType: string]: number }, sensorIndex: AverageSensorIndex) => {
        return {
          ...acc,
          [sensorIndex.component.replace('.index', '')]: sensorIndex.averageIndex,
        };
      },
      {}
    );

    return this.AWAIR_OMNI_SENSOR_TYPES.reduce((acc: Sensors, sensorType: Measurement) => {
      return {
        ...acc,
        [sensorType]: {
          value: typeof sensorValues[sensorType] === 'number' ? sensorValues[sensorType].toFixed() : '-',
          index: typeof sensorIndices[sensorType] === 'number' ? Math.floor(Math.abs(sensorIndices[sensorType])) : null,
        },
      };
    }, {});
  };

  getSnapshotEntities = (allLocations: GroupWithAirData[]): { [id: string]: SnapshotEntity } => {
    return allLocations.reduce((acc: { [id: string]: SnapshotEntity }, groupWithAirData: GroupWithAirData) => {
      return {
        ...acc,
        [groupWithAirData.name]: {
          // Key to name in order to sort by name
          name: groupWithAirData.name,
          score: groupWithAirData.averageScore !== 0 ? Math.round(groupWithAirData.averageScore) : null,
          sensors: this.getSnapshotSensors(
            groupWithAirData.averageSensorValues ?? [],
            groupWithAirData.averageSensorIndices ?? []
          ),
        },
      };
    }, {});
  };

  releaseTimer = () => {
    if (this.timer) {
      clearTimeout(this.timer);
    }
  };

  setTimer = (timeout: number, variables: SnapshotGridVariables) => {
    this.timer = setTimeout(() => {
      this.setState({
        variables,
      });
    }, timeout);
  };

  render() {
    const { variables } = this.state;

    return (
      <StSnapshotGridContainer>
        <Query<SnapshotGridData, SnapshotGridVariables>
          query={GET_AIR_DATA_OF_ALL_GROUPS}
          variables={variables}
          notifyOnNetworkStatusChange={true}>
          {({ error, data, networkStatus }: QueryResult<SnapshotGridData, SnapshotGridVariables>) => {
            if (networkStatus === NetworkStatus.ready) {
              this.releaseTimer();
              this.setTimer(airDataTimeInterval, this.getVariables());
            }

            if (error) {
              return <span>{error}</span>;
            }

            const snapshotEntities: { [id: string]: SnapshotEntity } =
              (data && data.org && data.org.locations && this.getSnapshotEntities(data.org.locations)) || {};

            const snapshotGrid: React.ReactNode = Object.keys(snapshotEntities)
              .sort()
              .map((snapshotKey: string) => {
                const snapshot: SnapshotEntity = snapshotEntities[snapshotKey];
                const snapshotSensors: React.ReactNode = Object.keys(snapshot.sensors).map((sensorKey: string) => {
                  return (
                    <SensorDotLabel
                      key={sensorKey}
                      value={snapshot.sensors[sensorKey].value}
                      index={snapshot.sensors[sensorKey].index}
                      type={sensorKey as Measurement}
                    />
                  );
                });

                return (
                  <Snapshot header={snapshot.name} key={snapshotKey}>
                    <ScoreDotLabel
                      value={snapshot.score != null ? `${snapshot.score}` : '–'}
                      condition={scoreToCondition(snapshot.score)}
                      style={{ marginBottom: '32px' }}
                    />
                    {snapshotSensors}
                  </Snapshot>
                );
              });

            return <>{snapshotGrid}</>;
          }}
        </Query>
      </StSnapshotGridContainer>
    );
  }
}

export default withAfbApollo(SnapshotGridContainer);



// WEBPACK FOOTER //
// ./src/app/dashboard/containers/SnapshotGrid.container.tsx